import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Upload, Sparkles, Download, ImagePlus, Lock, Check, ShoppingBag, Facebook, Twitter, MessageCircle, Linkedin, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import { absoluteUrl } from "../lib/api";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORIGINAL_URL = "https://customer-assets.emergentagent.com/job_authentic-papicoo/artifacts/3iavnkvk_Capture%20d%E2%80%99e%CC%81cran%C2%A0.%202026-06-02%20a%CC%80%2001.05.49.jpeg.png";
const FIRST_RESULT_PATH = "/papicoo-portrait.png";

/** Génère un fingerprint client persistant en localStorage. */
function getFingerprint() {
  let fp = localStorage.getItem("papicoo_portrait_fp");
  if (!fp) {
    fp = "fp_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("papicoo_portrait_fp", fp);
  }
  return fp;
}

export default function Portrait() {
  const navigate = useNavigate();
  const location = useLocation();
  const fingerprint = useMemo(() => getFingerprint(), []);
  const fileRef = useRef(null);

  const [catalog, setCatalog] = useState([]);
  const [packages, setPackages] = useState([]);
  const [style, setStyle] = useState("presidentiel-paris");
  const [originalUrl, setOriginalUrl] = useState(ORIGINAL_URL);
  const [originalBase64, setOriginalBase64] = useState(null);
  const [resultUrl, setResultUrl] = useState(FIRST_RESULT_PATH);
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // package_id
  const [buyerEmail, setBuyerEmail] = useState("");

  const loadQuota = async () => {
    try {
      const { data } = await axios.post(`${API}/portrait/quota`, { fingerprint });
      setQuota(data);
    } catch (e) { debugLog("portrait quota load failed:", e?.message); }
  };

  useEffect(() => {
    axios.get(`${API}/portrait/styles`).then((r) => setCatalog(r.data || [])).catch((e) => debugLog("styles fetch:", e?.message));
    axios.get(`${API}/portrait/packages`).then((r) => setPackages(r.data || [])).catch((e) => debugLog("packages fetch:", e?.message));
    loadQuota();
    // Chargement initial unique
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retour de Stripe checkout — crédite le quota
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    const paid = params.get("paid");
    if (paid === "1" && sessionId) {
      (async () => {
        try {
          const { data } = await axios.get(`${API}/portrait/checkout/status/${sessionId}`);
          if (data.paid) {
            toast.success(`✅ Paiement confirmé — ${data.credited_photos || ""} portraits crédités !`);
            await loadQuota();
            // Nettoie l'URL
            navigate("/portrait", { replace: true });
          }
        } catch (e) { console.warn("portrait checkout status:", e?.message); }
      })();
    } else if (params.get("cancelled")) {
      toast.info(T("Paiement annulé — tu peux réessayer quand tu veux."));
      navigate("/portrait", { replace: true });
    }
    // Effet unique au montage pour traiter le retour Stripe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setOriginalUrl(reader.result);
      setOriginalBase64(reader.result);
      setResultUrl("");
      toast.success(T("Photo chargée. Choisis un style et lance la transformation."));
    };
    reader.readAsDataURL(f);
  };

  const generate = async () => {
    if (quota && !quota.can_generate) {
      setShowPaywall(true);
      return;
    }
    setLoading(true);
    setResultUrl("");
    try {
      const payload = { style, extra_prompt: extra, fingerprint };
      if (originalBase64) payload.image_base64 = originalBase64;
      else payload.image_url = originalUrl;
      const { data } = await axios.post(`${API}/portrait/enhance`, payload, { timeout: 120000 });
      setResultUrl(data.image_url);
      toast.success(T("Portrait livré 🌹"));
      await loadQuota();
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowPaywall(true);
      } else {
        toast.error(e?.response?.data?.detail || T("La transformation a échoué."));
      }
    } finally { setLoading(false); }
  };

  const buyPack = async (pkgId) => {
    setCheckoutLoading(pkgId);
    try {
      const { data } = await axios.post(`${API}/portrait/checkout`, {
        package_id: pkgId,
        fingerprint,
        origin_url: window.location.origin,
        buyer_email: buyerEmail,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Impossible de lancer le paiement. Réessaie."));
      setCheckoutLoading(null);
    }
  };

  const finalSrc = resultUrl?.startsWith("/api/") ? absoluteUrl(resultUrl) : resultUrl;
  const canGenerate = !quota || quota.can_generate;
  const isFreeNext = quota && quota.free_remaining > 0 && quota.paid_remaining === 0;

  const shareUrl = finalSrc;
  const shareText = "Mon portrait par Papicoo — papicoo.com/portrait 🌹";

  const shareLinks = useMemo(() => ({
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://papicoo.com/portrait")}&quote=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent("https://papicoo.com/portrait")}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + " " + (shareUrl || "https://papicoo.com/portrait"))}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://papicoo.com/portrait")}`,
  }), [shareUrl]);

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} https://papicoo.com/portrait`);
      toast.success(T("Lien copié."));
    } catch { toast.error(T("Copie impossible.")); }
  };

  const downloadImage = async () => {
    if (!finalSrc) return;
    try {
      const r = await fetch(finalSrc);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `papicoo-${style}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(T("Téléchargement impossible.")); }
  };

  return (
    <div data-testid="page-portrait" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Portrait Studio — Papicoo")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
            {T("Ton vrai visage.")}<br />
            <span className="italic font-light">{T("Élevé au style que tu choisis.")}</span>
          </h1>
        </div>
        {quota && (
          <div data-testid="quota-badge" className="papicoo-card px-4 py-3 text-xs">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Tes portraits")}</div>
            <div className="mt-1 flex gap-3 items-baseline">
              <span><strong className="font-display text-xl text-[color:var(--papicoo-ink)]">{quota.free_remaining}</strong> {T("gratuit")}</span>
              <span className="text-[color:var(--papicoo-muted)]">·</span>
              <span><strong className="font-display text-xl text-[color:var(--papicoo-gold)]">{quota.paid_remaining}</strong> {T("achetés")}</span>
            </div>
          </div>
        )}
      </div>

      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl leading-relaxed">
        {T("Visage 100% préservé par l'IA. Téléverse une photo, choisis ton style — présidentiel, mode, culturel, sport, mariage, LinkedIn — et Papicoo transforme.")} {isFreeNext && <strong className="text-[color:var(--papicoo-ink)]">{T("Premier portrait offert.")}</strong>}
      </p>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* ORIGINAL */}
        <div className="papicoo-card overflow-hidden">
          <div className="px-5 pt-4 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Original — authentique")}</div>
            <button data-testid="upload-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 text-xs text-[color:var(--papicoo-ink)] hover:underline">
              <Upload size={14} /> {T("Changer la photo")}
            </button>
            <input ref={fileRef} data-testid="file-input" type="file" accept="image/*" hidden onChange={onFile} />
          </div>
          <div className="mt-3 aspect-[4/5] bg-[color:var(--papicoo-sand-deep)] overflow-hidden">
            {originalUrl ? (
              <img src={originalUrl} alt={T("Original")} data-testid="img-original" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[color:var(--papicoo-muted)]"><ImagePlus size={32} /></div>
            )}
          </div>
        </div>

        {/* RESULT */}
        <div className="papicoo-card overflow-hidden">
          <div className="px-5 pt-4 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-gold)]">{T("Transformé — par Papicoo IA")}</div>
            {finalSrc && !loading && (
              <button data-testid="download-portrait" onClick={downloadImage} className="inline-flex items-center gap-2 text-xs text-[color:var(--papicoo-ink)] hover:underline">
                <Download size={14} /> {T("Télécharger")}
              </button>
            )}
          </div>
          <div className="mt-3 aspect-[4/5] bg-[color:var(--papicoo-sand-deep)] overflow-hidden relative">
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm bg-[color:var(--papicoo-sand)]/60 z-10">
                <div className="text-center">
                  <Loader2 size={28} className="animate-spin text-[color:var(--papicoo-ink)] mx-auto" />
                  <div className="mt-3 text-xs text-[color:var(--papicoo-muted)] italic">{T("Papicoo travaille — force tranquille…")}</div>
                </div>
              </div>
            )}
            {finalSrc ? (
              <img src={finalSrc} alt={T("Portrait")} data-testid="img-result" className="w-full h-full object-cover" />
            ) : !loading && (
              <div className="w-full h-full flex items-center justify-center text-[color:var(--papicoo-muted)] text-sm italic px-5 text-center">
                {T("Choisis un style et lance la transformation.")}
              </div>
            )}
          </div>

          {/* SHARE BAR */}
          {finalSrc && !loading && (
            <div data-testid="share-bar" className="px-5 py-4 border-t border-[color:var(--papicoo-border)] bg-[color:var(--papicoo-sand)]">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] mb-2 inline-flex items-center gap-1">
                <Share2 size={12} /> {T("Partage ton portrait")}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a data-testid="share-fb" href={shareLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-105 transition" aria-label={T("Partager sur Facebook")}><Facebook size={16} /></a>
                <a data-testid="share-wa" href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-105 transition" aria-label={T("Partager sur WhatsApp")}><MessageCircle size={16} /></a>
                <a data-testid="share-tw" href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#0F0F0F] text-white hover:scale-105 transition" aria-label={T("Partager sur X/Twitter")}><Twitter size={16} /></a>
                <a data-testid="share-li" href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[#0A66C2] text-white hover:scale-105 transition" aria-label={T("Partager sur LinkedIn")}><Linkedin size={16} /></a>
                <button data-testid="share-copy" onClick={copyShareLink} className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:scale-105 transition" aria-label={T("Copier le lien")}><Link2 size={16} /></button>
                <span className="text-[11px] italic text-[color:var(--papicoo-muted)] ml-2">+1 partage = +baraka.</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CATALOGUE STYLES — par catégorie */}
      <div className="mt-8 papicoo-card p-6">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Choisis ton style — librement")}</div>
        <div className="mt-4 space-y-5">
          {catalog.map((cat) => (
            <div key={cat.category}>
              <div className="text-sm font-medium text-[color:var(--papicoo-ink)] mb-2">{cat.category}</div>
              <div className="flex flex-wrap gap-2">
                {cat.items.map((s) => (
                  <button
                    key={s.slug}
                    data-testid={`style-${s.slug}`}
                    onClick={() => setStyle(s.slug)}
                    className={`px-4 py-2 text-xs rounded-full transition-all inline-flex items-center gap-1.5 ${style === s.slug ? "papicoo-btn-gold" : "bg-white border border-[color:var(--papicoo-border)] text-[color:var(--papicoo-ink)] hover:border-[color:var(--papicoo-ink)]"}`}
                  >
                    <span className="text-base leading-none">{s.emoji}</span>
                    <span>{T(s.label)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <label className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Direction artistique (optionnel)")}</label>
          <input
            data-testid="extra-prompt"
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder={T("Ex : cravate or, regard légèrement vers la gauche, ombre douce…")}
            className="w-full mt-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30"
          />
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button
            data-testid="generate-portrait"
            onClick={generate}
            disabled={loading}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${canGenerate ? "papicoo-btn-primary" : "bg-[color:var(--papicoo-ink)]/40 text-white cursor-pointer"} disabled:opacity-50`}
          >
            {(() => {
              if (loading) return <Loader2 size={16} className="animate-spin" />;
              return canGenerate ? <Sparkles size={16} /> : <Lock size={16} />;
            })()}
            {(() => {
              if (loading) return "Transformation…";
              if (!canGenerate) return T("Débloquer plus de portraits");
              return isFreeNext ? "Lancer mon essai gratuit" : "Transformer mon portrait";
            })()}
          </button>
          {quota && !canGenerate && (
            <span className="text-xs text-[color:var(--papicoo-accent)] italic">{T("Essai gratuit utilisé ✓ Choisis un pack ci-dessous.")}</span>
          )}
        </div>
      </div>

      {/* PRICING — packs */}
      <div data-testid="pricing-section" className="mt-10">
        <div className="text-center mb-5">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Continue après l'essai")}</div>
          <h2 className="font-display text-3xl text-[color:var(--papicoo-ink)] mt-1">{T("3 packs simples, pas d'abonnement.")}</h2>
          <p className="text-sm text-[color:var(--papicoo-muted)] mt-1">{T("Paye une fois, utilise quand tu veux. Tes portraits sont à toi.")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {packages.map((p) => {
            const isPopular = p.id === "trio";
            const isCouple = p.id === "couple";
            const isFamily = p.id === "famille";
            let btnClass = "papicoo-btn-primary";
            if (isPopular) btnClass = "papicoo-btn-gold";
            else if (isCouple) btnClass = "bg-rose-400 text-white hover:bg-rose-500";
            else if (isFamily) btnClass = "bg-emerald-500 text-white hover:bg-emerald-600";
            return (
              <div key={p.id} data-testid={`pack-${p.id}`} className={`papicoo-card p-5 relative ${isPopular ? "ring-2 ring-[color:var(--papicoo-gold)]" : ""} ${isCouple ? "ring-2 ring-rose-300" : ""} ${isFamily ? "ring-2 ring-emerald-300" : ""}`}>
                {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)]">{T("Le plus choisi")}</div>}
                {isCouple && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full bg-rose-400 text-white">{T("Couple 💐")}</div>}
                {isFamily && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] uppercase tracking-[0.15em] rounded-full bg-emerald-500 text-white">{T("Famille 👨‍👩‍👧‍👦")}</div>}
                <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{p.label.split("—")[0].trim()}</div>
                <div className="mt-2 font-display text-4xl text-[color:var(--papicoo-ink)]">
                  {p.price_eur.toFixed(2).replace(".", ",")}<span className="text-base align-top"> €</span>
                </div>
                <div className="text-sm text-[color:var(--papicoo-muted)] mt-1">{p.photos} portrait{p.photos > 1 ? "s" : ""}{isCouple ? T(" · pour les deux 💍") : ""}</div>
                <ul className="mt-4 space-y-1.5 text-xs text-[color:var(--papicoo-ink)]">
                  <li className="inline-flex items-center gap-2"><Check size={12} className="text-[color:var(--papicoo-gold)]" /> {T("Tous les styles débloqués")}</li>
                  <li className="inline-flex items-center gap-2"><Check size={12} className="text-[color:var(--papicoo-gold)]" /> {T("Téléchargement haute résolution")}</li>
                  <li className="inline-flex items-center gap-2"><Check size={12} className="text-[color:var(--papicoo-gold)]" /> {T("Visage 100% préservé")}</li>
                  {isCouple && <li className="inline-flex items-center gap-2"><Check size={12} className="text-rose-400" /> {T("Style cohérent pour les deux")}</li>}
                  <li className="inline-flex items-center gap-2"><Check size={12} className="text-[color:var(--papicoo-gold)]" /> {T("Partage 1 clic réseaux sociaux")}</li>
                </ul>
                <button
                  data-testid={`buy-${p.id}`}
                  onClick={() => buyPack(p.id)}
                  disabled={checkoutLoading === p.id}
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full ${btnClass} text-sm disabled:opacity-60`}
                >
                  {checkoutLoading === p.id ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                  Choisir ce pack
                </button>
              </div>
            );
          })}
        </div>
        <div className="text-center text-[11px] italic text-[color:var(--papicoo-muted)] mt-4">
          {T("Paiement Stripe sécurisé · CB, Apple Pay, Google Pay · TVA incluse")}
        </div>
      </div>

      {/* PAYWALL MODAL */}
      {showPaywall && (
        <div data-testid="paywall-modal" className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowPaywall(false)}>
          <div className="papicoo-card p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Essai terminé")}</div>
            <div className="font-display text-2xl text-[color:var(--papicoo-ink)] mt-1">{T("Tu as aimé ? Continue avec un pack.")}</div>
            <p className="text-sm text-[color:var(--papicoo-muted)] mt-2">{T("Choisis ton pack ci-dessous, paye une fois et reviens transformer autant de fois que tu veux.")}</p>
            <button onClick={() => { setShowPaywall(false); document.querySelector('[data-testid="pricing-section"]')?.scrollIntoView({ behavior: "smooth" }); }} className="mt-5 w-full papicoo-btn-primary px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-2">
              <ShoppingBag size={14} /> {T("Voir les packs")}
            </button>
            <button onClick={() => setShowPaywall(false)} className="mt-2 w-full text-xs text-[color:var(--papicoo-muted)]">{T("Plus tard")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
